import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Clock, RefreshCw, Filter } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const ACTION_LABELS = {
  'tier_access.update': 'Tier access matrix updated',
  'commission_settings.update': 'Commission settings changed',
  'user.tier_change': 'User tier changed',
  'user.suspend': 'User suspended',
  'user.unsuspend': 'User unsuspended',
};

/**
 * Admin audit log viewer. Lists mutations recorded by `record_audit` in
 * reverse chronological order with a simple action filter + pagination.
 */
export default function AuditLog() {
  const { getAuthHeader } = useAuth();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(page * PAGE_SIZE) });
      if (actionFilter) params.set('action', actionFilter);
      const res = await axios.get(`${API_URL}/api/admin/audit-log?${params.toString()}`, {
        headers: getAuthHeader(),
      });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, actionFilter]);

  const formatTime = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const formatDiff = (before, after) => {
    if (!before && !after) return null;
    const keys = new Set([
      ...Object.keys(before || {}),
      ...Object.keys(after || {}),
    ]);
    const rows = [];
    for (const k of keys) {
      const b = before?.[k];
      const a = after?.[k];
      if (JSON.stringify(b) === JSON.stringify(a)) continue;
      rows.push({ key: k, before: b, after: a });
    }
    if (rows.length === 0) return null;
    return (
      <div className="text-xs space-y-1 mt-1">
        {rows.slice(0, 6).map(r => (
          <div key={r.key} className="font-mono">
            <span className="text-muted-foreground">{r.key}:</span>{' '}
            <span className="line-through text-rose-400/80">{JSON.stringify(r.before)}</span>
            {' → '}
            <span className="text-emerald-400">{JSON.stringify(r.after)}</span>
          </div>
        ))}
        {rows.length > 6 && <div className="text-muted-foreground">+{rows.length - 6} more</div>}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="audit-log-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-playfair font-bold text-foreground mb-2 flex items-center gap-2">
            <Clock className="w-6 h-6 text-primary" /> Audit Log
          </h2>
          <p className="text-muted-foreground text-sm">
            Every commission change, tier access update, user suspension, and manual tier promotion is recorded here.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={actionFilter}
              onChange={(e) => { setPage(0); setActionFilter(e.target.value); }}
              placeholder="Filter action (e.g. tier_access)"
              className="pl-8 h-9 text-sm w-[230px]"
              data-testid="audit-filter-input"
            />
          </div>
          <Button variant="outline" onClick={load} data-testid="audit-reload-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Reload
          </Button>
        </div>
      </div>

      <div className="wm-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3 font-semibold">When</th>
              <th className="text-left p-3 font-semibold">Who</th>
              <th className="text-left p-3 font-semibold">Action</th>
              <th className="text-left p-3 font-semibold">Target</th>
              <th className="text-left p-3 font-semibold">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">
                <RefreshCw className="inline w-4 h-4 mr-2 animate-spin" /> Loading…
              </td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No entries yet.</td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-border/50 hover:bg-muted/20 align-top">
                <td className="p-3 whitespace-nowrap text-muted-foreground font-mono text-xs">{formatTime(e.created_at)}</td>
                <td className="p-3 text-foreground">{e.actor_email}</td>
                <td className="p-3">
                  <span className="inline-block text-xs uppercase tracking-widest px-2 py-0.5 rounded-sm bg-gold/10 border border-gold/30 text-gold font-semibold">
                    {ACTION_LABELS[e.action] || e.action}
                  </span>
                </td>
                <td className="p-3 font-mono text-xs text-muted-foreground">{e.target || '—'}</td>
                <td className="p-3">
                  {formatDiff(e.before, e.after) || (
                    e.metadata && Object.keys(e.metadata).length ? (
                      <pre className="text-xs text-muted-foreground whitespace-pre-wrap">{JSON.stringify(e.metadata)}</pre>
                    ) : <span className="text-muted-foreground text-xs">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
            Previous
          </Button>
          <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
